// re.rejouis.fr — RETOURNER UN ARTICLE (retours après achat sur rejouis.fr).
// Deep-link /retour (lien du compte rejouis.fr). Le client connecté choisit sa commande,
// les articles et le motif ; il voit le remboursement estimé (retenues comprises) puis
// reçoit tout de suite son QR code Mondial Relay. Le retour est créé dans Shopify.
// Défectueux / erreur de commande : pas de renvoi d'office — le client montre le problème
// (photo ou vidéo) et choisit sa solution (remboursement, remplacement, crédit +10 %) ;
// l'équipe valide depuis le lab.
// Règles (délais, retenues, catégories) : src/lib/retours.js — recalculées par le serveur.
import { useEffect, useMemo, useRef, useState } from 'react';
import { CocheAnimee, EcranHeader, Icons, Spinner, Sticker, Thumb } from '../components/ui.jsx';
import EtiquetteQr from '../components/EtiquetteQr.jsx';
import {
  chargerCommandesRetour, creerRetour, envoyerPreuves, equivalentsRetour, etiquettePdfRetour, pointsRelaisRetour,
  repondreReclamation, choisirReclamation, annulerReclamation,
} from '../lib/api.js';
import { fmtEuro } from '../lib/format.js';
import { suivre } from '../lib/posthog.js';
import {
  MOTIFS, PREUVES_MAX, PREUVE_TAILLE_MAX, PREUVE_TYPES, RETENUE_OUVERT, RETENUE_TRANSPORT, SOLUTIONS,
  calculRetour, montantsReclamation,
} from '../lib/retours.js';

export const LIEN_AIDE_RETOUR = 'https://rejouis.fr/blogs/news-1/retourner-un-article';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function Etiquette({ commande, etiquette, reclamation = false, acceptee = false }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="card" style={{ padding: 16 }}>
        {etiquette.labelUrl ? (
          <a className="btn btn-primary btn-block" href={etiquette.labelUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
            <Icons.download size={16} /> Imprimer mon étiquette
          </a>
        ) : (
          <EtiquetteQr qrCode={etiquette.qrCode} demanderEtiquettePdf={() => etiquettePdfRetour(commande.id)} />
        )}
      </div>
      <div className="soft-card" style={{ padding: 14, fontSize: 13, color: 'var(--encre-doux)', lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, color: 'var(--noir)', marginBottom: 4 }}>Comment préparer ton colis</div>
        1. Remets le ou les articles dans leur boîte, avec tous leurs accessoires (câble, pochette…).<br />
        2. Emballe le tout discrètement et ferme bien le colis.<br />
        3. Dépose-le dans <strong>n’importe quel point relais Mondial Relay</strong> avec ton QR code.<br />
        Pas besoin de glisser un mot : ta référence <strong>{etiquette.ref}</strong> est déjà sur l’étiquette.
      </div>
      {etiquette.suiviUrl ? (
        <a href={etiquette.suiviUrl} target="_blank" rel="noopener noreferrer" className="btn btn-tertiary btn-block" style={{ textDecoration: 'none' }}>
          <Icons.truck size={16} /> Suivre mon colis
        </a>
      ) : null}
      <div style={{ fontSize: 12, color: 'var(--stone)', lineHeight: 1.55, textAlign: 'center' }}>
        {acceptee ? 'Ta réclamation est déjà réglée : le colis sert seulement à nous rendre l’article.'
          : reclamation ? 'On vérifie l’article dès son arrivée et on te donne notre réponse.' : 'On te rembourse dès que le colis est arrivé et que l’article est contrôlé.'}
      </div>
    </div>
  );
}

const LIBELLE_SOLUTION = { remboursement: 'un remboursement', remplacement: 'un remplacement', credit: 'un crédit boutique' };

// Où en est la réclamation (défectueux) : en examen, acceptée ou refusée par l'équipe.
// Photos / vidéos d'un défaut (réclamation, puis réponse à une demande de précisions).
function ChoixPreuves({ preuves, setPreuves, setErreur, disabled }) {
  const input = useRef(null);
  function ajouter(e) {
    const ajout = Array.from(e.target.files || []);
    e.target.value = '';
    const refuses = ajout.filter((f) => !PREUVE_TYPES.test(f.type) || f.size > PREUVE_TAILLE_MAX);
    setErreur(refuses.length ? 'Photos ou vidéos uniquement, 100 Mo maximum par fichier : envoie une vidéo plus courte.' : null);
    setPreuves((p) => [...p, ...ajout.filter((f) => !refuses.includes(f))].slice(0, PREUVES_MAX));
  }
  return (
    <>
      <div className="ph-no-capture" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {preuves.map((f, i) => (
          <div key={`${f.name}-${i}`} className="card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', fontSize: 13 }}>
            {f.type.startsWith('image/') ? <Icons.camera size={16} /> : <Icons.pack size={16} />}
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <span style={{ fontSize: 11.5, color: 'var(--stone)' }}>{(f.size / 1024 / 1024).toFixed(1)} Mo</span>
            <button onClick={() => setPreuves((p) => p.filter((_, j) => j !== i))} disabled={disabled} aria-label="Retirer"
              style={{ background: 'none', border: 0, padding: 2, cursor: 'pointer', color: 'var(--stone)' }}><Icons.x size={16} /></button>
          </div>
        ))}
      </div>
      {preuves.length < PREUVES_MAX ? (
        <>
          <input ref={input} type="file" accept="image/*,video/*" multiple onChange={ajouter} style={{ display: 'none' }} />
          <button className="btn btn-secondary btn-block" onClick={() => input.current?.click()} disabled={disabled}>
            <Icons.plus size={16} /> {preuves.length ? 'Ajouter un autre fichier' : 'Ajouter une photo ou une vidéo'}
          </button>
        </>
      ) : null}
    </>
  );
}

// Réponse du client à une demande de précisions de l'équipe (7 jours).
function ReponseInfos({ c, onRepondu }) {
  const [reponse, setReponse] = useState('');
  const [preuves, setPreuves] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [progres, setProgres] = useState(null);
  const [erreur, setErreur] = useState(null);
  const pret = !envoi && (reponse.trim().length >= 5 || preuves.length > 0);

  async function envoyer() {
    if (!pret) return;
    setEnvoi(true);
    setErreur(null);
    try {
      let envoyees = [];
      if (preuves.length) { setProgres(0); envoyees = await envoyerPreuves(preuves, setProgres); }
      await repondreReclamation(c.id, reponse.trim(), envoyees);
      suivre('retour_reclamation_reponse', { preuves: preuves.length });
      onRepondu();
    } catch (e) {
      setErreur(`${e.message || 'Une erreur est survenue, réessaie dans un instant.'} Si ça persiste, écris-nous à contact@rejouis.fr.`);
    } finally { setEnvoi(false); setProgres(null); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <textarea value={reponse} onChange={(e) => setReponse(e.target.value.slice(0, 1000))} rows={4} placeholder="Ta réponse"
        style={{ width: '100%', padding: 12, borderRadius: 12, border: '1px solid var(--bord)', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
      <ChoixPreuves preuves={preuves} setPreuves={setPreuves} setErreur={setErreur} disabled={envoi} />
      {erreur ? <div style={{ fontSize: 13, color: 'var(--rouge-sombre)' }}>{erreur}</div> : null}
      <button className="btn btn-primary btn-block" disabled={!pret} onClick={envoyer}>
        {envoi ? (progres != null && progres < 1 ? `Envoi des fichiers… ${Math.round(progres * 100)} %` : <Spinner size={16} />) : 'Envoyer ma réponse'}
      </button>
    </div>
  );
}

function StatutReclamation({ r, c, onMaj }) {
  const montant = r.solution !== 'remplacement' && r.montant ? ` de ${fmtEuro(r.montant)}` : '';
  const limite = r.echeance ? fmtDate(r.echeance) : '';
  const titre = {
    en_examen: 'Réclamation en cours d’examen', infos: 'On a besoin de précisions', retour: 'Renvoie-nous l’article',
    acceptee: 'Réclamation acceptée', refusee: 'Réclamation refusée', annulee: 'Réclamation annulée',
    traitee: 'Réponse de l’équipe', choix: 'Choisis ta solution',
  }[r.statut];
  const texte = r.statut === 'choix'
    ? `Désolés pour ce souci ! Voici ce qu’on te propose : choisis ce qui te convient avant le ${limite}.`
    : r.statut === 'en_examen' && r.choixClient && r.solution === 'remboursement'
    ? `C’est noté : tu as choisi le remboursement${montant}. On le lance sous 48 h (jours ouvrés) et on te confirme par e-mail.`
    : r.statut === 'en_examen' && r.choixClient
    ? 'C’est noté, on finalise ton choix et on te confirme par e-mail.'
    : r.statut === 'en_examen' && !r.solution
    ? 'On regarde ta demande et on revient vers toi par e-mail sous 48 h (jours ouvrés), avec une étiquette de retour gratuite si on a besoin de récupérer l’article. Garde-le d’ici là.'
    : r.statut === 'en_examen'
    ? `On regarde tes preuves et on revient vers toi par e-mail sous 48 h (jours ouvrés). Tu as demandé ${LIBELLE_SOLUTION[r.solution] || 'une solution'}${montant}. Garde l’article d’ici là.`
    : r.statut === 'traitee'
      ? r.message || 'On t’a répondu par e-mail.'
    : r.statut === 'infos'
      ? `Réponds avant le ${limite}, sinon ta réclamation sera annulée.`
    : r.statut === 'retour'
      ? `On a besoin de récupérer l’article. Dépose ton colis avant le ${limite} avec ce QR code (transport offert), sinon ta réclamation sera annulée. On te donne notre réponse dès réception.`
    : r.statut === 'acceptee' && r.solution === 'remplacement'
      ? 'Ton article de remplacement est en préparation, tu recevras un e-mail à son expédition.'
    : r.statut === 'acceptee' && r.solution === 'credit'
      ? `${r.montant ? fmtEuro(r.montant) : 'Ton crédit'} de crédit boutique ${r.montant ? 'ont été ajoutés' : 'a été ajouté'} à ton compte rejouis.fr, sans date limite.`
    : r.statut === 'acceptee' && r.solution === 'remboursement'
      ? `Ton remboursement${montant} part sur ton moyen de paiement (quelques jours selon ta banque).`
    : r.statut === 'acceptee'
      ? 'On t’a envoyé le détail par e-mail.'
    : r.statut === 'annulee' && r.raisonAnnulation === 'client'
      ? 'Tu as annulé ta demande. Besoin de nous recontacter ? Tu peux en refaire une ci-dessous.'
    : r.statut === 'annulee'
      ? `${{ non_depose: 'Le colis n’a pas été déposé', sans_choix: 'Tu n’as pas choisi de solution' }[r.raisonAnnulation] || 'On n’a pas eu ta réponse'} dans les 7 jours, ta réclamation a été annulée. Tu peux en refaire une ci-dessous.`
      : `Ta réclamation n’a pas été acceptée${r.motifRefus ? ' :' : '.'}`;
  // Refus : le motif écrit par l'équipe dans son propre bloc, la suite sur une ligne à part.
  const suiteRefus = r.statut === 'refusee' ? 'Tu peux en refaire une avec d’autres preuves, ou nous écrire à contact@rejouis.fr.' : null;
  // Acceptée : que faire de l'article (choisi par l'équipe, cf. lab reclamations.js).
  const article = r.statut !== 'acceptee' ? null
    : r.recuperer === false && r.motif === 'erreur' ? 'Inutile de nous renvoyer l’article reçu par erreur : tu peux le garder.'
    : r.recuperer === false ? 'Inutile de nous renvoyer l’article : garde-le et dépose-le au recyclage. S’il a une batterie ou des piles, direction un point de collecte des petits appareils électriques (bacs en magasin, déchetterie).'
    : r.recuperer && r.etiquette ? 'On a besoin de récupérer l’article : dépose ton colis avec ce QR code dans n’importe quel point relais Mondial Relay (transport offert).'
    : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="soft-card" style={{ padding: 14, fontSize: 13, color: 'var(--encre-doux)', lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, color: r.statut === 'refusee' || r.statut === 'annulee' ? 'var(--rouge-sombre)' : 'var(--noir)', marginBottom: 4 }}>{titre}</div>
        {r.statut === 'infos' && r.question ? <div style={{ fontWeight: 600, color: 'var(--noir)', marginBottom: 6 }}>« {r.question} »</div> : null}
        {r.statut === 'choix' && r.proposition?.message ? (
          <div style={{ margin: '4px 0 8px', padding: '8px 12px', borderLeft: '3px solid var(--rouge)', background: '#fff', borderRadius: 8, color: 'var(--noir)' }}>« {r.proposition.message} »</div>
        ) : null}
        {texte}
        {suiteRefus && r.motifRefus ? (
          <div style={{ margin: '8px 0', padding: '8px 12px', borderLeft: '3px solid var(--rouge)', background: '#fff', borderRadius: 8, color: 'var(--noir)', fontWeight: 600 }}>« {r.motifRefus} »</div>
        ) : null}
        {suiteRefus ? <div style={{ marginTop: r.motifRefus ? 0 : 6 }}>{suiteRefus}</div> : null}
        {article ?<div style={{ marginTop: 6 }}>{article}</div> : null}
      </div>
      {r.statut === 'infos' && c ? <ReponseInfos c={c} onRepondu={onMaj} /> : null}
      {r.statut === 'choix' && c && r.proposition ? <ChoixProposition c={c} p={r.proposition} onChoisi={onMaj} /> : null}
      {c && ['en_examen', 'infos', 'retour', 'choix'].includes(r.statut) ? <AnnulerDemande c={c} r={r} onAnnule={onMaj} /> : null}
      {(r.statut === 'retour' || (r.statut === 'acceptee' && r.recuperer)) && c && r.etiquette
        ? <Etiquette commande={c} etiquette={r.etiquette} reclamation={r.statut === 'retour'} acceptee={r.statut === 'acceptee'} /> : null}
    </div>
  );
}

// Annuler sa réclamation à tout moment (changé d'avis, tout était bon, il garde le produit),
// avec une courte justification lue par l'équipe dans le lab. Pas d'e-mail.
const RAISONS_ANNULATION = ['Tout était bon finalement', 'Je garde le produit', 'J’ai changé d’avis'];
function AnnulerDemande({ c, r, onAnnule }) {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const pret = !envoi && texte.trim().length >= 3;

  async function confirmer() {
    if (!pret) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await annulerReclamation(c.id, texte.trim());
      suivre('retour_reclamation_annulee', { statut: r.statut, motif: r.motif });
      onAnnule();
    } catch (e) {
      setErreur(`${e.message || 'Une erreur est survenue, réessaie dans un instant.'} Si ça persiste, écris-nous à contact@rejouis.fr.`);
    } finally { setEnvoi(false); }
  }

  if (!ouvert) {
    return (
      <button onClick={() => setOuvert(true)} style={{ alignSelf: 'center', fontSize: 12.5, fontWeight: 600, color: 'var(--stone)', textDecoration: 'underline', padding: '4px 0' }}>
        Annuler ma demande
      </button>
    );
  }
  return (
    <div className="soft-card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>Annuler ma demande</div>
      <div style={{ fontSize: 12.5, color: 'var(--encre-doux)', lineHeight: 1.5 }}>
        Dis-nous en quelques mots pourquoi : ça nous aide à nous améliorer.
        {r.statut === 'retour' ? ' Tu as déjà déposé le colis ? Écris-nous plutôt à contact@rejouis.fr.' : ''}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {RAISONS_ANNULATION.map((x) => (
          <button key={x} onClick={() => setTexte(x)} className="badge"
            style={{ padding: '6px 10px', fontSize: 11.5, background: texte === x ? 'var(--rouge)' : '#fff', color: texte === x ? '#fff' : 'var(--noir)', border: '1px solid var(--bord)' }}>{x}</button>
        ))}
      </div>
      <textarea value={texte} onChange={(e) => setTexte(e.target.value.slice(0, 500))} rows={2} placeholder="Ou écris ta raison"
        style={{ width: '100%', padding: 10, borderRadius: 12, border: '1px solid var(--bord)', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
      {erreur ? <div style={{ fontSize: 13, color: 'var(--rouge-sombre)' }}>{erreur}</div> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setOuvert(false)} disabled={envoi}>Garder ma demande</button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={confirmer} disabled={!pret}>{envoi ? <Spinner size={16} /> : 'Confirmer l’annulation'}</button>
      </div>
    </div>
  );
}

// Solutions proposées par l'équipe (erreur de commande) : le client choisit la sienne. Montants
// et renvoi de l'article fixés par la proposition ; échange → article + lieu de livraison.
function ChoixProposition({ c, p, onChoisi }) {
  const [solution, setSolution] = useState(p.options.length === 1 ? p.options[0] : null);
  const [choix, setChoix] = useState({});
  const [relais, setRelais] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const lignes = c.articles.filter((a) => p.articleIds.includes(a.id));
  const libelles = {
    remboursement: [`Remboursement de ${fmtEuro(p.montants.remboursement)}`, 'Sur ton moyen de paiement.'],
    credit: [`${fmtEuro(p.montants.credit)} de crédit boutique`, 'Tout de suite sur ton compte rejouis.fr (+10 %), sans date limite.'],
    remplacement: ['Un autre article à la place', 'Tu le choisis ci-dessous, on te l’envoie gratuitement.'],
  };
  const pret = !envoi && solution && (solution !== 'remplacement'
    || (lignes.length > 0 && lignes.every((l) => choix[l.id]) && (c.livraison?.mode !== 'relais' || relais)));
  const manque = !solution ? 'Choisis une solution.'
    : solution === 'remplacement' && !lignes.every((l) => choix[l.id]) ? 'Choisis l’article que tu veux recevoir.'
    : solution === 'remplacement' && c.livraison?.mode === 'relais' && !relais ? 'Choisis le point relais où le recevoir.' : null;

  async function valider() {
    if (!pret) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await choisirReclamation(c.id, solution, solution === 'remplacement' ? choix : undefined, solution === 'remplacement' ? relais : undefined);
      suivre('retour_reclamation_choix', { solution, options: p.options.length });
      onChoisi();
    } catch (e) {
      setErreur(`${e.message || 'Une erreur est survenue, réessaie dans un instant.'} Si ça persiste, écris-nous à contact@rejouis.fr.`);
    } finally { setEnvoi(false); }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {p.options.map((o) => (
        <button key={o} onClick={() => setSolution(o)} className="card"
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, textAlign: 'left', border: solution === o ? '2px solid var(--rouge)' : undefined }}>
          <span style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, border: `2px solid ${solution === o ? 'var(--rouge)' : 'var(--stone)'}`, display: 'grid', placeItems: 'center' }}>
            {solution === o ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--rouge)' }} /> : null}
          </span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>{libelles[o]?.[0]}</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--stone)', marginTop: 2 }}>{libelles[o]?.[1]}</span>
          </span>
        </button>
      ))}
      {solution === 'remplacement' ? (
        <ChoixRemplacement c={c} lignes={lignes} choix={choix} setChoix={setChoix} relais={relais} setRelais={setRelais} libelleIdentique="L’article que tu avais commandé" />
      ) : null}
      <div className="soft-card" style={{ padding: 12, fontSize: 12.5, lineHeight: 1.55, color: 'var(--encre-doux)' }}>
        {p.recuperer
          ? 'On récupère l’article reçu par erreur : après ton choix, tu recevras un QR code pour le déposer en point relais Mondial Relay (transport offert).'
          : 'Tu peux garder l’article reçu par erreur, inutile de nous le renvoyer.'}
      </div>
      {erreur ? <div style={{ fontSize: 13, color: 'var(--rouge-sombre)' }}>{erreur}</div> : null}
      {manque && solution ? <div style={{ fontSize: 12.5, color: 'var(--stone)', textAlign: 'center' }}>{manque}</div> : null}
      <button className="btn btn-primary btn-block" disabled={!pret} onClick={valider}>
        {envoi ? <Spinner size={16} /> : 'Valider mon choix'}
      </button>
    </div>
  );
}

// Remplacement : article choisi par ligne (identique s'il en reste, sinon équivalents) et
// lieu de livraison. Même mode que la commande d'origine : Colissimo à domicile → même
// adresse ; Mondial Relay → le client choisit son point (Boxtal ne nous dit pas lequel il
// avait pris, mais son point habituel ressort dans la liste autour de son code postal).
function ChoixRemplacement({ c, lignes, choix, setChoix, relais, setRelais, libelleIdentique = 'Le même article' }) {
  const [options, setOptions] = useState({}); // articleId → options[] | 'erreur'
  const [cp, setCp] = useState(c.livraison?.cp || '');
  const [points, setPoints] = useState(null);
  const [recherche, setRecherche] = useState(false);
  const [erreurRelais, setErreurRelais] = useState(null);
  const ids = lignes.map((l) => l.id).join(',');

  useEffect(() => {
    for (const l of lignes) {
      if (options[l.id]) continue;
      equivalentsRetour(c.id, l.id)
        .then((o) => {
          setOptions((x) => ({ ...x, [l.id]: o }));
          if (o.length === 1) setChoix((x) => ({ ...x, [l.id]: o[0].variantId }));
        })
        .catch(() => setOptions((x) => ({ ...x, [l.id]: 'erreur' })));
    }
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  async function chercher() {
    setRecherche(true);
    setErreurRelais(null);
    try {
      const p = await pointsRelaisRetour(cp.trim(), c.livraison?.pays || 'FR');
      // Le serveur revérifie le point en refaisant CETTE recherche (10 résultats autour du
      // code postal cherché, pas forcément autour de celui du point).
      setPoints(p.map((x) => ({ ...x, cpRecherche: cp.trim() })));
      if (!p.length) setErreurRelais('Aucun point relais trouvé pour ce code postal.');
    } catch (e) { setErreurRelais(e.message); } finally { setRecherche(false); }
  }
  useEffect(() => { if (c.livraison?.mode === 'relais' && cp) chercher(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {lignes.map((l) => {
        const o = options[l.id];
        return (
          <div key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, padding: '0 2px' }}>À la place de « {l.titre} »</div>
            {o == null ? <Spinner size={16} label="Recherche des articles disponibles…" />
              : o === 'erreur' ? <div style={{ fontSize: 12.5, color: 'var(--rouge-sombre)' }}>Impossible de charger les articles, réessaie.</div>
              : !o.length ? <div style={{ fontSize: 12.5, color: 'var(--rouge-sombre)', lineHeight: 1.5 }}>Aucun article équivalent en stock pour le moment : choisis plutôt le remboursement ou le crédit +10 %.</div>
              : o.map((x) => (
                <button key={x.variantId} onClick={() => setChoix((ch) => ({ ...ch, [l.id]: x.variantId }))} className="card"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, textAlign: 'left', border: choix[l.id] === x.variantId ? '2px solid var(--rouge)' : undefined }}>
                  <Thumb produit={x} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.35 }}>{x.titre}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--stone)' }}>{x.identique ? libelleIdentique : 'Article équivalent'} · {fmtEuro(x.prix)}</div>
                  </div>
                </button>
              ))}
          </div>
        );
      })}

      <div style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 2px 0' }}>Où le recevoir ?</div>
      {c.livraison?.mode !== 'relais' ? (
        <div className="soft-card ph-no-capture" style={{ padding: 12, fontSize: 12.5, lineHeight: 1.55, color: 'var(--encre-doux)' }}>
          À domicile, comme ta commande ({c.livraison?.titre || 'Colissimo'}) : {c.livraison?.adresse}.
          Une autre adresse ? Écris-nous à contact@rejouis.fr.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--stone)', lineHeight: 1.5, padding: '0 2px' }}>
            En point relais Mondial Relay, comme ta commande. Ton point habituel est sûrement dans la liste, sinon cherche autour d’un autre code postal.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={cp} onChange={(e) => setCp(e.target.value.slice(0, 8))} inputMode="numeric" placeholder="Code postal"
              onKeyDown={(e) => { if (e.key === 'Enter' && cp.trim().length >= 4) chercher(); }}
              style={{ flex: 1, padding: 10, borderRadius: 12, border: '1px solid var(--bord)', fontFamily: 'inherit', fontSize: 14 }} />
            <button className="btn btn-secondary" onClick={chercher} disabled={recherche || cp.trim().length < 4}>{recherche ? <Spinner size={14} /> : 'Chercher'}</button>
          </div>
          {erreurRelais ? <div style={{ fontSize: 12.5, color: 'var(--rouge-sombre)' }}>{erreurRelais}</div> : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(points || []).map((p) => (
              <button key={p.id} onClick={() => setRelais(p)} className="card"
                style={{ padding: 10, textAlign: 'left', fontSize: 12.5, lineHeight: 1.45, border: relais?.id === p.id ? '2px solid var(--rouge)' : undefined }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{p.nom}</div>
                <div style={{ color: 'var(--stone)' }}>{p.adresse}, {p.cp} {p.ville}{p.distance ? ` · ${(p.distance / 1000).toFixed(1).replace('.', ',')} km` : ''}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CarteCommande({ c, onOuvrir }) {
  const dispo = c.articles.filter((a) => a.quantite > 0);
  return (
    <button onClick={c.indisponible ? undefined : onOuvrir} disabled={!!c.indisponible} className="card"
      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: 12, opacity: c.indisponible ? 0.55 : 1, cursor: c.indisponible ? 'default' : 'pointer' }}>
      <div style={{ display: 'flex' }}>
        {(dispo.length ? dispo : c.articles).slice(0, 3).map((a, i) => (
          <div key={a.id} style={{ marginLeft: i ? -14 : 0, borderRadius: 12, border: '2px solid var(--blanc)' }}><Thumb produit={a} size={44} /></div>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>Commande {c.nom}</div>
        <div style={{ fontSize: 12, color: 'var(--stone)' }}>Le {fmtDate(c.date)} · {c.articles.length} article{c.articles.length > 1 ? 's' : ''}</div>
      </div>
      {c.etiquette ? <span className="badge badge-creme">Retour en cours</span>
        : c.reclamation?.statut === 'en_examen' ? <span className="badge badge-creme">Réclamation en cours</span>
        : c.reclamation?.statut === 'infos' ? <span className="badge badge-creme">Réponse attendue</span>
        : c.reclamation?.statut === 'choix' ? <span className="badge badge-rouge">Solution à choisir</span>
        : c.reclamation?.statut === 'retour' ? <span className="badge badge-creme">Article à renvoyer</span>
        : c.indisponible ? <span className="badge badge-stone" style={{ textAlign: 'right' }}>{c.indisponible}</span>
        : <Icons.chev size={18} />}
    </button>
  );
}

function Formulaire({ c, onCree }) {
  const [motif, setMotif] = useState(null);
  const [choix, setChoix] = useState({}); // id → { quantite, ouvert }
  const [note, setNote] = useState('');
  const [compris, setCompris] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [preuves, setPreuves] = useState([]); // File[] (défectueux)
  const [solution, setSolution] = useState(null);
  const [progres, setProgres] = useState(null); // envoi des preuves, 0..1
  const [remplacements, setRemplacements] = useState({}); // articleId → variantId
  const [relais, setRelais] = useState(null);

  const lignes = useMemo(() => c.articles
    .filter((a) => choix[a.id])
    .map((a) => ({ ...a, quantite: choix[a.id].quantite, ouvert: motif === 'avis' && !!choix[a.id].ouvert })), [c, choix, motif]);
  const calcul = motif && lignes.length ? calculRetour(motif, lignes) : null;
  const m = MOTIFS.find((x) => x.id === motif);
  const reclamation = !!m?.preuve;
  const avecSolution = !!m?.choixSolution;
  const montants = montantsReclamation(lignes);
  const remplacementIdentique = lignes.length > 0 && lignes.every((l) => l.remplacementDispo);
  const noteOk = !m?.noteObligatoire || note.trim().length >= 5;
  const remplacementOk = solution !== 'remplacement'
    || (lignes.every((l) => remplacements[l.id]) && (c.livraison?.mode !== 'relais' || relais));
  // Ce qui manque encore, affiché sous le bouton (un bouton grisé sans explication bloquait
  // les clients : retour de test 2026-10-21).
  const manque = !motif ? null
    : !lignes.length ? 'Choisis au moins un article.'
    : !noteOk ? 'Décris le problème en quelques mots (étape 3).'
    : reclamation && !preuves.length ? 'Ajoute au moins une photo ou une vidéo (étape 4).'
    : reclamation && avecSolution && !solution ? 'Choisis ta solution (étape 5).'
    : reclamation && avecSolution && !remplacementOk ? (lignes.every((l) => remplacements[l.id]) ? 'Choisis le point relais où recevoir ton article.' : 'Choisis l’article de remplacement.')
    : !compris ? (reclamation ? 'Coche la case pour certifier ta déclaration.' : 'Coche la case pour confirmer.')
    : null;
  const pret = motif && lignes.length && calcul && !calcul.refus && !manque && !envoi;

  function basculer(a) {
    setChoix((ch) => {
      const n = { ...ch };
      if (n[a.id]) delete n[a.id]; else n[a.id] = { quantite: 1, ouvert: null };
      return n;
    });
  }
  const majChoix = (id, champ, v) => setChoix((ch) => ({ ...ch, [id]: { ...ch[id], [champ]: v } }));
  const ouvertManquant = motif === 'avis' && lignes.some((l) => choix[l.id].ouvert == null);

  async function envoyer() {
    if (!pret || ouvertManquant) return;
    setEnvoi(true);
    setErreur(null);
    try {
      let envoyees;
      if (reclamation) {
        setProgres(0);
        envoyees = await envoyerPreuves(preuves, setProgres);
      }
      const res = await creerRetour({
        orderId: c.id, motif, note: note.trim(),
        lignes: lignes.map((l) => ({ id: l.id, quantite: l.quantite, ouvert: l.ouvert })),
        ...(reclamation ? { preuves: envoyees, solution: avecSolution ? solution : null } : {}),
        ...(reclamation && avecSolution && solution === 'remplacement' ? { remplacements, relais } : {}),
      });
      if (reclamation) {
        suivre('retour_reclamation_creee', {
          motif, solution: avecSolution ? solution : null, articles: lignes.length, montant: res.reclamation?.montant,
          preuves_video: preuves.filter((f) => f.type.startsWith('video/')).length,
          remplacement_identique: remplacementIdentique,
        });
      } else suivre('retour_cree', { motif, articles: lignes.length, remboursement_estime: res.calcul?.total });
      onCree(res);
    } catch (e) {
      suivre('retour_erreur', { motif, statut: e.status });
      // Le renvoi vers le mail seulement pour une vraie panne (envoi, Shopify), pas pour un oubli.
      const panne = !e.status || e.status >= 500;
      setErreur(`${e.message || 'Une erreur est survenue, réessaie dans un instant.'}${reclamation && panne ? ' Si ça persiste, écris-nous à contact@rejouis.fr avec ta photo ou ta vidéo.' : ''}`);
    } finally {
      setEnvoi(false);
      setProgres(null);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="t-overline" style={{ padding: '0 2px' }}>1 · Pourquoi ce retour ?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {MOTIFS.map((x) => {
          const possible = c.motifs[x.id];
          const actif = motif === x.id;
          return (
            <button key={x.id} disabled={!possible} onClick={() => { setMotif(x.id); setCompris(false); setErreur(null); }} className="card"
              style={{ padding: 12, textAlign: 'left', opacity: possible ? 1 : 0.5, border: actif ? '2px solid var(--rouge)' : undefined }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{x.label}</div>
              <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 2, lineHeight: 1.5 }}>
                {x.id === 'avis'
                  ? (possible ? `Jusqu’au ${fmtDate(c.finAvis)} · ${fmtEuro(RETENUE_TRANSPORT)} retenus pour le transport` : 'Délai de 14 jours après réception dépassé')
                  : x.id === 'erreur' ? 'Une photo de ce que tu as reçu, on revient vers toi'
                  : x.preuve ? 'Pas de renvoi d’office : une photo ou une vidéo du défaut suffit'
                  : 'Retour gratuit'}
              </div>
            </button>
          );
        })}
      </div>

      {motif ? (
        <>
          <div className="t-overline" style={{ padding: '6px 2px 0' }}>2 · Quels articles ?</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {c.articles.map((a) => {
              const sel = !!choix[a.id];
              const indispo = a.quantite < 1 || a.regle === 'exclu';
              return (
                <div key={a.id} className="card" style={{ padding: 12, opacity: indispo ? 0.5 : 1, border: sel ? '2px solid var(--rouge)' : undefined }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: indispo ? 'default' : 'pointer' }}>
                    <input type="checkbox" checked={sel} disabled={indispo} onChange={() => basculer(a)} style={{ width: 18, height: 18, accentColor: 'var(--rouge)' }} />
                    <Thumb produit={a} size={44} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.35 }}>{a.titre}</div>
                      <div style={{ fontSize: 12, color: 'var(--stone)' }}>
                        {a.quantite < 1 ? 'Déjà retourné' : a.regle === 'exclu' ? 'Non retournable' : `${fmtEuro(a.prix)}${a.quantite > 1 ? ` · ${a.quantite} retournables` : ''}`}
                      </div>
                    </div>
                  </label>
                  {sel && a.quantite > 1 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
                      Quantité
                      <select value={choix[a.id].quantite} onChange={(e) => majChoix(a.id, 'quantite', Number(e.target.value))}>
                        {Array.from({ length: a.quantite }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                      </select>
                    </div>
                  ) : null}
                  {sel && motif === 'avis' ? (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>Le sachet plastique a-t-il été ouvert ?</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {[[false, 'Non, scellé'], [true, 'Oui, ouvert']].map(([v, lib]) => (
                          <button key={lib} onClick={() => majChoix(a.id, 'ouvert', v)} className={choix[a.id].ouvert === v ? 'btn btn-primary' : 'btn btn-secondary'}
                            style={{ flex: 1, padding: '8px 10px', fontSize: 13 }}>{lib}</button>
                        ))}
                      </div>
                      {choix[a.id].ouvert ? (
                        <div style={{ fontSize: 12, marginTop: 6, lineHeight: 1.5, color: a.regle === 'hygiene' ? 'var(--rouge-sombre)' : 'var(--encre-doux)' }}>
                          {a.regle === 'hygiene' ? 'Produit d’hygiène : une fois ouvert, il ne peut pas être repris.'
                            : a.regle === 'sans_retenue' ? 'Pas de retenue pour cet article, même ouvert.'
                            : `${fmtEuro(RETENUE_OUVERT)} retenus : un article ouvert doit être reconditionné.`}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div className="t-overline" style={{ padding: '6px 2px 0' }}>3 · {m.noteObligatoire ? 'Décris le problème' : 'Un détail à ajouter ? (facultatif)'}</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} rows={3}
            placeholder={motif === 'defectueux' ? 'Ex. : ne s’allume plus après la charge' : motif === 'erreur' ? 'Ex. : reçu la mauvaise couleur' : ''}
            style={{ width: '100%', padding: 12, borderRadius: 12, border: '1px solid var(--bord)', fontFamily: 'inherit', fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
        </>
      ) : null}

      {reclamation ? (
        <>
          <div className="t-overline" style={{ padding: '6px 2px 0' }}>4 · {motif === 'erreur' ? 'Montre-nous ce que tu as reçu' : 'Montre-nous le défaut'}</div>
          <div style={{ fontSize: 12.5, color: 'var(--encre-doux)', lineHeight: 1.55, padding: '0 2px' }}>
            {motif === 'erreur'
              ? 'Une photo de l’article reçu (et de son étiquette ou de sa boîte si possible). Garde-le pour l’instant : si on a besoin de le récupérer, on t’envoie une étiquette de retour gratuite.'
              : 'Une courte vidéo où l’on voit le problème (le jouet qui ne s’allume pas, qui ne charge plus…) ou une photo de la casse. Garde l’article : le plus souvent, tu n’as rien à nous renvoyer (si on a besoin de le récupérer, on t’envoie une étiquette de retour gratuite).'}
          </div>
          <ChoixPreuves preuves={preuves} setPreuves={setPreuves} setErreur={setErreur} disabled={envoi} />

          {avecSolution ? (<>
          <div className="t-overline" style={{ padding: '6px 2px 0' }}>5 · Ta solution</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SOLUTIONS.map((s) => {
              const actif = solution === s.id;
              const detail = s.id === 'remplacement'
                ? (remplacementIdentique ? 'Le même article, ou un équivalent si tu préfères, envoyé sans frais.' : 'Ce modèle n’est plus en stock : choisis un article équivalent, envoyé sans frais.')
                : s.detail;
              const montant = s.id === 'credit' ? montants.credit : s.id === 'remboursement' ? montants.remboursement : null;
              return (
                <button key={s.id} onClick={() => setSolution(s.id)} disabled={!lignes.length} className="card"
                  style={{ padding: 12, textAlign: 'left', display: 'flex', gap: 10, alignItems: 'center', opacity: lignes.length ? 1 : 0.5, border: actif ? '2px solid var(--rouge)' : undefined }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 2, lineHeight: 1.5 }}>{detail}</div>
                  </div>
                  {montant != null && lignes.length ? <div style={{ fontWeight: 800, fontSize: 15, color: s.id === 'credit' ? 'var(--rouge)' : 'var(--noir)' }}>{fmtEuro(montant)}</div> : null}
                </button>
              );
            })}
          </div>
          {solution === 'remplacement' && lignes.length ? (
            <ChoixRemplacement c={c} lignes={lignes} choix={remplacements} setChoix={setRemplacements} relais={relais} setRelais={setRelais} />
          ) : null}
          </>) : null}
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5, cursor: 'pointer', padding: '0 2px' }}>
            <input type="checkbox" checked={compris} onChange={(e) => setCompris(e.target.checked)} style={{ marginTop: 3, accentColor: 'var(--rouge)' }} />
            <span>Je certifie que ces fichiers montrent l’article reçu et que ma déclaration est sincère.</span>
          </label>
        </>
      ) : null}

      {calcul && !reclamation ? (
        <div className="soft-card" style={{ padding: 14, fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Ton remboursement estimé</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Articles retournés</span><span>{fmtEuro(calcul.brut)}</span></div>
          {calcul.retenueTransport ? <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Frais de retour</span><span>− {fmtEuro(calcul.retenueTransport)}</span></div> : null}
          {calcul.lignes.filter((l) => l.retenue).map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Sachet ouvert · {l.titre}</span><span>− {fmtEuro(l.retenue)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, borderTop: '1px solid var(--bord)', marginTop: 6, paddingTop: 6 }}>
            <span>Remboursé</span><span>{fmtEuro(calcul.total)}</span>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--stone)', lineHeight: 1.5, marginTop: 4 }}>
            Sur ton moyen de paiement, après réception et contrôle de l’article.
          </div>
          {calcul.refus ? <div style={{ color: 'var(--rouge-sombre)', marginTop: 6 }}>{calcul.refus}</div> : null}
          {!calcul.refus ? (
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={compris} onChange={(e) => setCompris(e.target.checked)} style={{ marginTop: 3, accentColor: 'var(--rouge)' }} />
              <span>J’ai compris{calcul.total < calcul.brut ? ' les retenues appliquées' : ''} et j’envoie ces articles dans un seul colis.</span>
            </label>
          ) : null}
        </div>
      ) : null}

      {erreur ? <div style={{ fontSize: 13, color: 'var(--rouge-sombre)' }}>{erreur}</div> : null}
      {motif ? (
        <button className="btn btn-primary btn-block" disabled={!pret || ouvertManquant} onClick={envoyer}>
          {envoi ? (progres != null && progres < 1 ? `Envoi des fichiers… ${Math.round(progres * 100)} %` : <Spinner size={16} />)
            : reclamation ? 'Envoyer ma réclamation' : 'Recevoir mon QR code de retour'}
        </button>
      ) : null}
      {reclamation && calcul?.refus ? <div style={{ fontSize: 13, color: 'var(--rouge-sombre)' }}>{calcul.refus}</div> : null}
      {motif && ouvertManquant ? <div style={{ fontSize: 12, color: 'var(--stone)', textAlign: 'center' }}>Indique pour chaque article si le sachet a été ouvert.</div>
        : motif && manque && !envoi ? <div style={{ fontSize: 12.5, color: 'var(--stone)', textAlign: 'center' }}>{manque}</div> : null}
    </div>
  );
}

export default function Retour({ go, client, onSeConnecter, commandeInitiale }) {
  const [commandes, setCommandes] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [ouverte, setOuverte] = useState(null); // id commande
  const [cree, setCree] = useState(null); // réponse de création

  const charger = () => {
    setErreur(null);
    chargerCommandesRetour()
      .then((cs) => {
        setCommandes(cs);
        if (commandeInitiale) {
          const c = cs.find((x) => x.id === commandeInitiale || x.id.endsWith(`/${commandeInitiale}`));
          if (c && !c.indisponible) setOuverte(c.id);
        }
      })
      .catch((e) => setErreur(e.message));
  };
  useEffect(() => { if (client) charger(); }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = (commandes || []).find((x) => x.id === ouverte) || null;
  const retour = () => {
    if (cree) { setCree(null); setOuverte(null); setCommandes(null); charger(); return; }
    if (ouverte) { setOuverte(null); return; }
    go('compte');
  };

  return (
    <div className="no-scrollbar anim-fade" style={{ height: '100%', overflow: 'auto', padding: '8px 0 40px' }}>
      <EcranHeader titre={c ? `Retour · commande ${c.nom}` : 'Retourner un article'} onBack={retour} />

      {!client ? (
        <div style={{ padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Sticker nom="boite" size={84} rot={-6} anim="pop" />
          <h2 className="t-display" style={{ fontSize: 24, margin: 0 }}>Connecte-toi pour<br /><span style={{ color: 'var(--rouge)' }}>retourner un article</span></h2>
          <p style={{ fontSize: 13.5, color: 'var(--stone)', lineHeight: 1.6, margin: 0 }}>
            Avec le compte rejouis.fr qui a passé la commande : tu choisis l’article, tu reçois ton QR code Mondial Relay.
          </p>
          <button className="btn btn-primary btn-block" onClick={onSeConnecter}>Se connecter avec Rejouis</button>
        </div>
      ) : erreur ? (
        <div style={{ padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="soft-card" style={{ padding: 14, fontSize: 13, color: 'var(--encre-doux)', lineHeight: 1.5 }}>
            Impossible de charger tes commandes pour l’instant. {erreur}
          </div>
          <button className="btn btn-secondary btn-block" onClick={charger}>Réessayer</button>
        </div>
      ) : commandes == null ? (
        <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}><Spinner size={20} label="Chargement de tes commandes…" /></div>
      ) : cree?.reclamation ? (
        <div style={{ padding: '4px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
            <CocheAnimee size={56} />
            <h2 className="t-display" style={{ fontSize: 22, margin: 0 }}>Réclamation envoyée</h2>
            <div style={{ fontSize: 13, color: 'var(--stone)' }}>On t’a envoyé un e-mail de confirmation.</div>
          </div>
          <StatutReclamation r={cree.reclamation} />
        </div>
      ) : cree ? (
        <div style={{ padding: '4px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 }}>
            <CocheAnimee size={56} />
            <h2 className="t-display" style={{ fontSize: 22, margin: 0 }}>Ton retour est enregistré</h2>
            <div style={{ fontSize: 13, color: 'var(--stone)' }}>
              Remboursement estimé : <strong style={{ color: 'var(--noir)' }}>{fmtEuro(cree.calcul?.total || 0)}</strong> · on t’a aussi envoyé ton QR code par e-mail.
            </div>
          </div>
          <Etiquette commande={c} etiquette={cree.etiquette} />
        </div>
      ) : c ? (
        <div style={{ padding: '4px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {c.etiquette ? (
            <>
              <div style={{ fontSize: 13.5, color: 'var(--encre-doux)', lineHeight: 1.6 }}>Un retour est déjà en cours pour cette commande. Voici ton étiquette.</div>
              <Etiquette commande={c} etiquette={c.etiquette} />
            </>
          ) : ['en_examen', 'infos', 'retour', 'choix'].includes(c.reclamation?.statut) ? <StatutReclamation r={c.reclamation} c={c} onMaj={charger} /> : (
            <>
              {c.reclamation ? <StatutReclamation r={c.reclamation} /> : null}
              <Formulaire c={c} onCree={(res) => setCree(res)} />
            </>
          )}
        </div>
      ) : commandes.length === 0 ? (
        <div style={{ padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Sticker nom="oeuf-etincelle" size={84} rot={6} anim="pop" />
          <h2 className="t-display" style={{ fontSize: 24, margin: 0 }}>Aucune commande à retourner</h2>
          <p style={{ fontSize: 13.5, color: 'var(--stone)', lineHeight: 1.6, margin: 0 }}>
            On n’affiche ici que les commandes expédiées de ce compte. Un souci avec une commande passée avec une autre adresse e-mail ? Écris-nous à{' '}
            <a href="mailto:contact@rejouis.fr" style={{ color: 'var(--rouge)', fontWeight: 600 }}>contact@rejouis.fr</a>.
          </p>
        </div>
      ) : (
        <div style={{ padding: '4px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--encre-doux)', lineHeight: 1.6, padding: '0 2px 4px' }}>
            Choisis la commande concernée. Produit défectueux ou erreur de notre part : montre-nous le problème en photo ou en vidéo, on revient vers toi.
            Changement d’avis : 14 jours après réception.{' '}
            <a href={LIEN_AIDE_RETOUR} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--rouge)', fontWeight: 600 }}>Tout savoir sur les retours</a>
          </div>
          {commandes.map((x) => <CarteCommande key={x.id} c={x} onOuvrir={() => { suivre('retour_commande_choisie', { en_cours: !!x.etiquette }); setOuverte(x.id); }} />)}
        </div>
      )}
    </div>
  );
}
